import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, Search } from "lucide-react";
import client, { extractError } from "@/api";

const STATUS_LABEL = {
  PENDING_REVIEW: "Pending review",
  APPROVED: "Approved",
  REJECTED: "Needs changes",
  CANCELLED: "Cancelled",
};

const FILTERS = [
  { value: "", label: "All" },
  { value: "PENDING_REVIEW", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

const DIRECTIONS = [
  { value: "buyer", label: "As buyer" },
  { value: "seller", label: "As seller" },
];

function RequestDetail({ id, onBack, onChanged }) {
  const [req, setReq] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    try { const r = await client.get(`/trade-requests/mine/${id}`); setReq(r.data); }
    catch (err) { setError(extractError(err, "Unable to load request.")); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!req) return <div className="loading-block">Loading…</div>;

  const cancel = async () => {
    setBusy(true); setError("");
    try {
      const r = await client.post(`/trade-requests/${id}/cancel`, { reason: reason || null });
      setReq(r.data); setMessage("Request cancelled."); onChanged?.();
    } catch (err) { setError(extractError(err, "Unable to cancel.")); }
    finally { setBusy(false); }
  };

  const canCancel = req.viewer_side === "BUYER" && req.status === "PENDING_REVIEW";

  return (
    <div className="admin-company-detail" data-testid="trade-request-detail">
      <button type="button" className="link-button back" data-testid="trade-request-back" onClick={onBack}><ChevronLeft size={14} /> Back to trade requests</button>
      <header className="detail-header">
        <div>
          <p className="eyebrow">{STATUS_LABEL[req.status]}</p>
          <h2>{req.listing_title}</h2>
          <p className="muted">Buyer: {req.buyer_name} · Seller: {req.seller_name}</p>
        </div>
        <div className="chip-stack">
          <span className={`status-chip status-${req.status.toLowerCase()}`}>{STATUS_LABEL[req.status]}</span>
        </div>
      </header>
      {error && <div className="form-error">{error}</div>}
      {message && <div className="form-success">{message}</div>}
      <section className="detail-grid">
        <article><p className="eyebrow">TRADE VALUE</p><p><strong>₹{req.trade_value.toLocaleString("en-IN")}</strong></p></article>
        <article><p className="eyebrow">FACILITATOR FEE ({req.facilitator_fee_rate_percent}%)</p><p>₹{req.facilitator_fee_amount.toLocaleString("en-IN")}</p></article>
        <article><p className="eyebrow">TOTAL CREDIT</p><p><strong>₹{req.total_credit_requirement.toLocaleString("en-IN")}</strong></p></article>
        <article><p className="eyebrow">QUANTITY</p><p>{req.requested_quantity ?? "—"} {req.quantity_unit || ""}</p></article>
        <article><p className="eyebrow">UNIT VALUE</p><p>₹{req.unit_value.toLocaleString("en-IN")}</p></article>
        <article><p className="eyebrow">CREATED</p><p>{new Date(req.created_at).toLocaleString()}</p></article>
        {req.buyer_note && <article className="wide"><p className="eyebrow">BUYER NOTE</p><p>{req.buyer_note}</p></article>}
        {req.admin_review_notes && <article className="wide"><p className="eyebrow">ADMIN NOTES</p><p>{req.admin_review_notes}</p></article>}
        {req.rejection_reason && <article className="wide"><p className="eyebrow">REJECTION REASON</p><p>{req.rejection_reason}</p></article>}
      </section>
      {canCancel && (
        <section className="section-block" data-testid="cancel-block">
          <p className="eyebrow">CANCEL REQUEST</p>
          <textarea placeholder="Optional reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason" />
          <button type="button" className="danger-button-solid" onClick={cancel} disabled={busy} data-testid="cancel-request-btn">Cancel request</button>
        </section>
      )}
      {req.audit_log?.length > 0 && (
        <section className="documents-section">
          <div className="section-heading"><div><p className="eyebrow">HISTORY</p><h3>Audit trail</h3></div></div>
          <ul className="audit-list" data-testid="trade-request-audit">
            {req.audit_log.map((a) => (
              <li key={a.id}>
                <strong>{a.event}</strong><small>{a.previous_state || "—"} → {a.new_state || "—"}</small>
                {a.reason && <p className="muted small">{a.reason}</p>}
                <small className="muted">{a.actor_email || "system"} · {new Date(a.created_at).toLocaleString()}</small>
              </li>
            ))}
          </ul>
        </section>
      )}
      {req.status === "APPROVED" && (
        <section className="section-block" data-testid="create-txn-block">
          <p className="eyebrow">NEXT STEP</p>
          <h3>Move this request into a transaction</h3>
          <p className="muted small">A transaction opens the fulfilment workflow and locks in the commercial terms above.</p>
          <button type="button" className="primary-button" data-testid="create-transaction-btn" disabled={busy}
            onClick={async () => {
              setBusy(true); setError("");
              try {
                const r = await client.post(`/transactions/${req.id}/create`, {});
                setMessage(`Transaction ${r.data.id.slice(0, 8)} created. Open the Transactions tab to continue.`);
              } catch (err) { setError(extractError(err)); }
              finally { setBusy(false); }
            }}
          >Create transaction</button>
        </section>
      )}
    </div>
  );
}

export default function TradeRequestsPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [direction, setDirection] = useState("buyer");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      params.set("direction", direction);
      const r = await client.get(`/trade-requests/mine?${params.toString()}`);
      setItems(r.data || []);
    } catch (err) { setError(extractError(err, "Unable to load trade requests.")); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, direction]);

  const counts = useMemo(() => {
    const c = {};
    items.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [items]);

  if (detailId) return <RequestDetail id={detailId} onBack={() => { setDetailId(null); load(); }} onChanged={load} />;

  return (
    <div className="my-listings-page" data-testid="trade-requests-page">
      <div className="admin-toolbar">
        <div className="filter-tabs" data-testid="trade-request-status-tabs">
          {FILTERS.map((f) => (
            <button key={f.value || "all"} type="button" data-testid={`trade-filter-${f.value || "all"}`} className={filter === f.value ? "active" : ""} onClick={() => setFilter(f.value)}>
              {f.label}{f.value && counts[f.value] > 0 && <span className="count">{counts[f.value]}</span>}
            </button>
          ))}
        </div>
        <div className="filter-tabs" data-testid="trade-direction-tabs">
          {DIRECTIONS.map((d) => (
            <button key={d.value} type="button" data-testid={`trade-direction-${d.value}`} className={direction === d.value ? "active" : ""} onClick={() => setDirection(d.value)}>{d.label}</button>
          ))}
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        <table className="admin-table" data-testid="trade-requests-table">
          <thead><tr><th>Listing</th><th>{direction === "buyer" ? "Seller" : "Buyer"}</th><th>Qty</th><th>Trade value</th><th>Fee (10%)</th><th>Total</th><th>Status</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="empty-row">No trade requests yet. Browse the marketplace and submit a request to get started.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} data-testid={`trade-request-row-${r.id}`}>
                <td><strong>{r.listing_title}</strong><br /><small className="muted">{r.listing_type}</small></td>
                <td>{direction === "buyer" ? r.seller_name : r.buyer_name}</td>
                <td>{r.requested_quantity ?? "—"} {r.quantity_unit || ""}</td>
                <td>₹{r.trade_value.toLocaleString("en-IN")}</td>
                <td>₹{r.facilitator_fee_amount.toLocaleString("en-IN")}</td>
                <td><strong>₹{r.total_credit_requirement.toLocaleString("en-IN")}</strong></td>
                <td><span className={`status-chip status-${r.status.toLowerCase()}`}>{STATUS_LABEL[r.status]}</span></td>
                <td><small>{new Date(r.created_at).toLocaleDateString()}</small></td>
                <td><button type="button" className="link-button" data-testid={`open-trade-request-${r.id}`} onClick={() => setDetailId(r.id)}>Open →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
